import Api from '@/vuex/api'
import { app } from '@/main'
import {
  ADD_RESERVATION_NOTE,
  CANCEL_LOT_RELOCATION,
  CANCEL_RESERVATION,
  CREATE_RESERVATION_CHARGE,
  EDIT_RESERVATION,
  LOAD_AVAILABILITY,
  LOAD_HISTORY,
  LOAD_RESERVATION,
  LOAD_RESERVATION_NOTES,
  LOAD_RESERVATION_NOTES_TOTAL,
  OPEN_MODAL,
  RESEND_RESERVATION,
  LOAD_RESERVATION_BREAKDOWNS,
  REFUND_RESERVATION,
  GENERATE_RESERVATION_INVOICE,
  SEND_CANCEL_MESSAGE,
  TRIP_DATA
} from '@/vuex/types'

const api = new Api()
const state = {
  reservation: {
    Account: {
      AccountType: {},
      Smartcard: {}
    },
    AdminResponsible: {},
    Car: {
      CarType: {
        AmazonImage: {}
      },
      Lot: {},
      CarHardware: {}
    },
    AccountNotes: [],
    AccountNotesTotal: 0,
    AccountCredits: [],
    CreditCharges: [],
    HeldCharge: {},
    FinanceCode: {},
    Lot: {},
    ReservationPromo: {},
    actualCost: 0.00,
    completed: false,
    completedEndDate: new Date().toISOString(),
    completedStartDate: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    endDate: new Date().toISOString(),
    extras: [],
    isPersonal: false,
    maintenance: false,
    memo: '',
    quotedCost: 0.00,
    refunded: false,
    reservationId: 0,
    startDate: new Date().toISOString(),
    availability: [],
    history: [],
    breakdowns: []
  },
  reservationActions: [
    { label: 'Resend Reservation', command: RESEND_RESERVATION, icon: 'fa fa-cloud-upload-alt' },
    { label: 'Request Trip Data', command: TRIP_DATA, icon: 'fa fa-cloud-download-alt' },
    { label: 'Edit Reservation', command: EDIT_RESERVATION, icon: 'fa fa-edit' },
    { label: 'Cancel Reservation', command: CANCEL_RESERVATION, icon: 'fa fa-ban' },
    { label: 'Cancel Reservation', command: SEND_CANCEL_MESSAGE, icon: 'fa fa-ban' },
    { label: 'Add Note', command: ADD_RESERVATION_NOTE, icon: 'fa fa-sticky-note' },
    { label: 'Create Reservation Charge', command: CREATE_RESERVATION_CHARGE, icon: 'fa fa-dollar-sign' },
    { label: 'Refund Reservation', command: REFUND_RESERVATION, icon: 'fa fa-exchange-alt' },
    { label: 'Generate Invoice', command: GENERATE_RESERVATION_INVOICE, icon: 'fa fa-file-alt' }
  ]
}

const getters = {
  loadedReservation (state) {
    return state.reservation
  },
  reservationNotes (state) {
    return state.reservation.AccountNotes
  },
  reservationNotesTotal (state) {
    return state.reservation.AccountNotesTotal
  },
  reservationActions: ({ reservationActions }) => (reservation, admin) => {
    let excludeActions = []
    let expired = !reservation.completed && !reservation.started && !reservation.cancelled && moment(reservation.endDate).isBefore(moment())
    if (reservation.cancelled || reservation.completed || expired) {
      excludeActions = excludeActions.concat([
        CANCEL_RESERVATION, RESEND_RESERVATION, EDIT_RESERVATION
      ])
      if (reservation.completed || moment(reservation.endDate).isBefore(moment()) || (reservation.maintenance && reservation.LotRelocationUuid)) {
        excludeActions.push(SEND_CANCEL_MESSAGE)
      }
    } else {
      excludeActions.push(GENERATE_RESERVATION_INVOICE)
    }
    if (reservation.refunded) {
      excludeActions.push(REFUND_RESERVATION)
    }
    if (!reservation.cancelled) {
      excludeActions.push(SEND_CANCEL_MESSAGE)
    }
    if (reservation.maintenance) {
      excludeActions = excludeActions.concat([
        CREATE_RESERVATION_CHARGE, REFUND_RESERVATION, GENERATE_RESERVATION_INVOICE
      ])
    }
    if (!admin.AccountType || !admin.AccountType.financeAdministrator) {
      excludeActions = excludeActions.concat([
        CREATE_RESERVATION_CHARGE, REFUND_RESERVATION
      ])
    }
    return reservationActions.filter(el => !excludeActions.includes(el.command))
  }
}

const mutations = {
  [LOAD_RESERVATION] (state, reservation) {
    state.reservation = reservation
  },
  [LOAD_AVAILABILITY] (state, availability) {
    state.reservation = { ...state.reservation, availability }
  },
  [LOAD_RESERVATION_BREAKDOWNS] (state, breakdowns) {
    state.reservation = { ...state.reservation, breakdowns }
  },
  [LOAD_HISTORY] (state, history) {
    state.reservation = { ...state.reservation, history }
  },
  [LOAD_RESERVATION_NOTES] (state, messages) {
    state.reservation = { ...state.reservation, AccountNotes: messages }
  },
  [LOAD_RESERVATION_NOTES_TOTAL] (state, count) {
    state.reservation = { ...state.reservation, AccountNotesTotal: count }
  }
}

const actions = {
  async [LOAD_RESERVATION] (context, reservationId) {
    let reservation = await api.getReservation(reservationId)
    context.commit(LOAD_RESERVATION, reservation)
  },
  async [LOAD_AVAILABILITY] (context, date, car = false) {
    if (!car) car = context.state.reservation.Car.uuid
    let availability = await api.getReservationAvailability(moment(date).format('x'), car)
    context.commit(LOAD_AVAILABILITY, availability)
  },
  async [LOAD_HISTORY] (context, reservationUuid = false) {
    if (!reservationUuid) reservationUuid = context.state.reservation.uuid
    let { history } = await api.getReservationHistory(reservationUuid)
    context.commit(LOAD_HISTORY, history)
  },
  async [LOAD_RESERVATION_BREAKDOWNS] (context, reservationUuid = false) {
    if (!reservationUuid) reservationUuid = context.state.reservation.uuid
    let { breakdowns } = await api.getReservationBreakdowns(reservationUuid)
    context.commit(LOAD_RESERVATION_BREAKDOWNS, breakdowns)
  },
  async [CREATE_RESERVATION_CHARGE] (context, reservation) {
    let chargeModal = {
      permalink: 'newCharge',
      options: {
        title: 'Create Charge',
        actionLabel: 'Create Charge'
      },
      params: {
        reservationUuid: reservation.uuid
      }
    }
    context.dispatch(OPEN_MODAL, chargeModal)
  },
  async [REFUND_RESERVATION] (context, reservation) {
    let refundReservationModal = {
      permalink: 'refundReservation',
      options: {
        title: 'Refund Reservation',
        actionLabel: 'Refund'
      },
      params: {
        reservationUuid: reservation.uuid
      }
    }
    context.dispatch(OPEN_MODAL, refundReservationModal)
  },
  async [GENERATE_RESERVATION_INVOICE] (context, reservation) {
    let invoiceModal = {
      permalink: 'reservationInvoice',
      options: {
        title: 'Reservation Invoice',
        actionLabel: 'Send Email'
      },
      params: {
        reservationUuid: reservation.uuid
      }
    }
    context.dispatch(OPEN_MODAL, invoiceModal)
  },
  async [ADD_RESERVATION_NOTE] (context, reservation) {
    let noteModal = {
      permalink: 'newNote',
      options: {
        title: 'Add Note',
        actionLabel: 'Add Note'
      },
      params: {
        accountUuid: reservation.Account.uuid,
        reservationUuid: reservation.uuid
      }
    }
    context.dispatch(OPEN_MODAL, noteModal)
  },
  async [EDIT_RESERVATION] (context, reservation) {
    if (reservation.maintenance && reservation.LotRelocation) {
      app.$router.push(`/fleet/relocate/edit/${reservation.LotRelocation.uuid}`)
    } else {
      app.$router.push(`/fleet/reservations/edit/${reservation.uuid}`)
    }
  },
  async [CANCEL_RESERVATION] (context, reservation) {
    if (reservation.maintenance && reservation.LotRelocation) {
      context.dispatch(CANCEL_LOT_RELOCATION, reservation.LotRelocation)
    } else {
      let cancelReservationModal = {
        permalink: 'cancelReservation',
        options: {
          title: 'Cancel Reservation',
          actionLabel: 'Confirm Cancel'
        },
        params: {
          reservationUuid: reservation.uuid
        }
      }
      context.dispatch(OPEN_MODAL, cancelReservationModal)
    }
  },
  async [SEND_CANCEL_MESSAGE] (context, reservation) {
    let cancelMessageModal = {
      permalink: 'sendCancelMessage',
      options: {
        title: 'Cancel Reservation',
        actionLabel: 'Resend'
      },
      params: {
        reservationUuid: reservation.uuid
      }
    }
    context.dispatch(OPEN_MODAL, cancelMessageModal)
  },
  async [RESEND_RESERVATION] (context, reservation) {
    await api.resendReservation(reservation.uuid)
    context.commit(OPEN_MODAL, {
      title: 'Success!',
      html: `
      <p>Reservation has been sent to vehicle again.</p>
      <p>Check Car Messages Log to see result.</p>
      `
    })
  },
  async [TRIP_DATA] (context, reservation) {
    await api.requestTripData(reservation.uuid)
    context.commit(OPEN_MODAL, {
      title: 'Success!',
      html: `
      <p>Request has been sent to vehicle again.</p>
      <p>Check Car Messages Log to see result.</p>
      `
    })
  }
}

export default {
  state,
  mutations,
  getters,
  actions
}



// WEBPACK FOOTER //
// ./src/vuex/modules/reservation.js